import React from "react";
import { Link } from "react-router-dom";
import { Clock, User } from "lucide-react";
import { formatDateShort, formatTime } from "@/lib/date";
import { TID } from "@/lib/testIds";

/**
 * Reusable news card.
 * variants: "default" | "compact" | "list" | "large"
 */
export default function NewsCard({ article, variant = "default", showSummary = true }) {
  if (!article) return null;
  const url = `/noticia/${article.slug}`;
  const catUrl = `/categoria/${article.main_category_slug}`;

  if (variant === "list") {
    return (
      <article data-testid={TID.newsCard(article.id)} className="group grid grid-cols-[100px_1fr] sm:grid-cols-[130px_1fr] gap-4 py-4 border-b border-softblue last:border-b-0">
        <Link to={url} data-testid={TID.newsCardLink(article.id)} className="block overflow-hidden bg-lightgray aspect-[4/3]">
          <img src={article.main_image} alt={article.title} loading="lazy" className="w-full h-full object-cover card-hover-image" style={{ objectPosition: article.image_focal_point || "50% 40%" }} />
        </Link>
        <div className="min-w-0">
          <Link to={catUrl} className="kicker hover:text-watergreen transition-colors">{article.main_category_name}</Link>
          <Link to={url}>
            <h3 className="mt-1 font-serif text-base sm:text-lg font-bold text-blueblack leading-snug line-clamp-3 group-hover:text-petroleum transition-colors">
              {article.title}
            </h3>
          </Link>
          <div className="mt-2 text-[11px] uppercase tracking-widest text-textgray flex items-center gap-3">
            <span>{formatDateShort(article.published_at)}</span>
            <span>{formatTime(article.published_at)} hs</span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "compact") {
    return (
      <article data-testid={TID.newsCard(article.id)} className="group flex flex-col">
        <Link to={url} data-testid={TID.newsCardLink(article.id)} className="block overflow-hidden bg-lightgray aspect-[16/10]">
          <img src={article.main_image} alt={article.title} loading="lazy" className="w-full h-full object-cover card-hover-image" style={{ objectPosition: article.image_focal_point || "50% 40%" }} />
        </Link>
        <div className="mt-3">
          <Link to={catUrl} className="kicker hover:text-watergreen transition-colors">{article.main_category_name}</Link>
          <Link to={url}>
            <h3 className="mt-1 font-serif text-base font-bold text-blueblack leading-snug line-clamp-3 group-hover:text-petroleum transition-colors">
              {article.title}
            </h3>
          </Link>
        </div>
      </article>
    );
  }

  if (variant === "large") {
    return (
      <article data-testid={TID.newsCard(article.id)} className="group flex flex-col">
        <Link to={url} data-testid={TID.newsCardLink(article.id)} className="block overflow-hidden bg-lightgray aspect-[16/10]">
          <img src={article.main_image} alt={article.title} loading="lazy" className="w-full h-full object-cover card-hover-image" style={{ objectPosition: article.image_focal_point || "50% 40%" }} />
        </Link>
        <div className="mt-4">
          <Link to={catUrl} className="kicker hover:text-watergreen transition-colors">{article.main_category_name}</Link>
          <Link to={url}>
            <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-bold text-blueblack leading-tight group-hover:text-petroleum transition-colors">
              {article.title}
            </h3>
          </Link>
          {showSummary && article.summary && (
            <p className="mt-2 text-sm text-textgray leading-relaxed line-clamp-3">{article.summary}</p>
          )}
          <div className="mt-3 text-[11px] uppercase tracking-widest text-textgray flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDateShort(article.published_at)} · {formatTime(article.published_at)} hs</span>
          </div>
        </div>
      </article>
    );
  }

  // default
  return (
    <article data-testid={TID.newsCard(article.id)} className="group flex flex-col bg-white border border-softblue">
      <Link to={url} data-testid={TID.newsCardLink(article.id)} className="block overflow-hidden bg-lightgray aspect-[16/10]">
        <img src={article.main_image} alt={article.title} loading="lazy" className="w-full h-full object-cover card-hover-image" style={{ objectPosition: article.image_focal_point || "50% 40%" }} />
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <Link to={catUrl} className="kicker hover:text-watergreen transition-colors">{article.main_category_name}</Link>
        <Link to={url}>
          <h3 className="mt-1.5 font-serif text-xl sm:text-2xl font-bold text-blueblack leading-snug group-hover:text-petroleum transition-colors">
            {article.title}
          </h3>
        </Link>
        {showSummary && article.summary && (
          <p className="mt-2 text-sm text-textgray leading-relaxed line-clamp-2">{article.summary}</p>
        )}
        <div className="mt-auto pt-3 text-[11px] uppercase tracking-widest text-textgray flex items-center gap-3">
          <span>{formatDateShort(article.published_at)}</span>
          <span>{formatTime(article.published_at)} hs</span>
        </div>
      </div>
    </article>
  );
}
